import {
    AttendeeSurvey,
    BUYER_ID,
    SurveyFormData,
    SurveyQuestion,
} from '../../../models/survey';
import { isRequiredQuestion } from '../../../utils/surveys';

interface SingleSurveyStatisticsForRequirementStatus {
    total: number;
    answered: number;
    ratio: number;
}

export interface SingleFormSurveyStatistics {
    optional: SingleSurveyStatisticsForRequirementStatus;
    required: SingleSurveyStatisticsForRequirementStatus;
    statsigMetrics: {
        optionalQuestionsTotal: string;
        optionalQuestionsAnswered: string;
        optionalQuestionsRatio: string;
        requiredQuestionsTotal: string;
    };
}

const getQuestionsGroupedByRequirementStatus = (
    questions: Array<SurveyQuestion>,
): Record<'required' | 'optional', Array<SurveyQuestion>> =>
    questions.reduce<Record<'required' | 'optional', Array<SurveyQuestion>>>(
        (accum, singleQuestion) =>
            isRequiredQuestion(singleQuestion, questions)
                ? {
                      ...accum,
                      required: [...accum.required, singleQuestion],
                  }
                : {
                      ...accum,
                      optional: [...accum.optional, singleQuestion],
                  },

        {
            required: [],
            optional: [],
        },
    );

const getAttendeeSurveysGroupedByRequirementStatus = (
    attendeeSurveys: Array<AttendeeSurvey>,
): {
    surveys: Array<Record<'required' | 'optional', AttendeeSurvey>>;
    questionsCount: {
        optional: number;
        required: number;
    };
} => {
    const surveys = attendeeSurveys.map(({ survey, ...commonProperties }) => {
        const { optional, required } =
            getQuestionsGroupedByRequirementStatus(survey);

        return {
            required: {
                ...commonProperties,
                survey: required,
            },
            optional: {
                ...commonProperties,
                survey: optional,
            },
        };
    });
    const optionalQuestionsCount = surveys.reduce(
        (sum, { optional }) => sum + optional.survey.length,
        0,
    );

    const requiredQuestionsCount = surveys.reduce(
        (sum, { required }) => sum + required.survey.length,
        0,
    );

    return {
        surveys,
        questionsCount: {
            optional: optionalQuestionsCount,
            required: requiredQuestionsCount,
        },
    };
};

const getAnsweredQuestionsCount = ({
    questionsByAttendee,
    formData,
}: {
    questionsByAttendee: Record<string, Array<SurveyQuestion>>;
    formData: SurveyFormData;
}) =>
    Object.entries(questionsByAttendee).reduce(
        (sum, [attendeeId, questions]) =>
            sum +
            questions.filter(
                ({ name }) =>
                    (
                        formData[attendeeId] as
                            | Record<string, unknown>
                            | undefined
                    )?.[name],
            ).length,
        0,
    );

const getAnsweredQuestionsStatistics = ({
    buyer,
    attendees,
    formData,
    requirementStatus,
}: {
    buyer: Record<'optional' | 'required', Array<SurveyQuestion>>;
    attendees: {
        surveys: Array<Record<'required' | 'optional', AttendeeSurvey>>;
        questionsCount: {
            optional: number;
            required: number;
        };
    };
    formData: SurveyFormData;
    requirementStatus: 'required' | 'optional';
}): SingleSurveyStatisticsForRequirementStatus => {
    const totalQuestions =
        attendees.questionsCount[requirementStatus] +
        buyer[requirementStatus].length;

    const answeredQuestions = getAnsweredQuestionsCount({
        questionsByAttendee: {
            ...Object.fromEntries(
                attendees.surveys.map(
                    ({ [requirementStatus]: { survey, id } }) => [id, survey],
                ),
            ),
            [BUYER_ID]: buyer[requirementStatus],
        },
        formData,
    });

    const ratio = totalQuestions > 0 ? answeredQuestions / totalQuestions : 1;

    return {
        total: totalQuestions,
        answered: answeredQuestions,
        ratio,
    };
};

export const getStatisticsFactory =
    ({
        attendeeSurveys,
        questionsForBuyer,
    }: {
        attendeeSurveys: Array<AttendeeSurvey>;
        questionsForBuyer: Array<SurveyQuestion>;
    }) =>
    (formData: SurveyFormData): SingleFormSurveyStatistics => {
        const buyer = getQuestionsGroupedByRequirementStatus(questionsForBuyer);
        const attendees =
            getAttendeeSurveysGroupedByRequirementStatus(attendeeSurveys);

        const optional = getAnsweredQuestionsStatistics({
            attendees,
            buyer,
            formData,
            requirementStatus: 'optional',
        });

        const required = getAnsweredQuestionsStatistics({
            attendees,
            buyer,
            formData,
            requirementStatus: 'required',
        });

        const optionalQuestionsRatio =
            optional.answered > 0
                ? `${(optional.ratio * 100).toFixed(2)}%`
                : '';

        return {
            optional,
            required,
            statsigMetrics: {
                optionalQuestionsTotal: optional.total.toString(),
                optionalQuestionsAnswered: optional.answered.toString(),
                optionalQuestionsRatio,
                requiredQuestionsTotal: required.total.toString(),
            },
        };
    };
